import { configureStore } from '@reduxjs/toolkit'
import { enableMapSet } from 'immer'
import userReducer from './UserStore'
import roomReducer from './RoomStore'
import computerReducer from './ComputerStore'
import whiteboardReducer from './WhiteboardStore'
import workspaceReducer from './WorkspaceStore'
import notificationReducer from './NotificationStore'
import entityCatalogueReducer from './EntityCatalogueStore'
import gameReducer from './GameStore'
import callReducer from './CallStore'
import virtualRoomAreasReducer from './VirtualRoomAreasStore'
import virtualRoomFilterReducer from './VirtualRoomFilterStore'
import teleportReducer from './TeleportStore'
import roomBookingReducer from './RoomBookingStore'
import richNotificationReducer from './RichNotificationStore'
import guestReducer from './GuestStore'
import onboardingReducer from './OnboardingStore'

// Enable Map and Set support in Immer (used by Redux Toolkit)
enableMapSet()

const store = configureStore({
  reducer: {
    user: userReducer,
    room: roomReducer,
    computer: computerReducer,
    whiteboard: whiteboardReducer,
    workspace: workspaceReducer,
    notification: notificationReducer,
    entityCatalogue: entityCatalogueReducer,
    game: gameReducer,
    call: callReducer,
    virtualRoomAreas: virtualRoomAreasReducer,
    virtualRoomFilter: virtualRoomFilterReducer,
    teleport: teleportReducer,
    roomBooking: roomBookingReducer,
    richNotification: richNotificationReducer,
    guest: guestReducer,
    onboarding: onboardingReducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        // Disable serializable check for specific action types and paths that
        // might include non-serializable values like dates, Maps, or MediaStream objects
        ignoredActions: [
          'webrtc/', // All webrtc actions
          'user/', // All user actions
          'room/', // All room actions
          'video/', // All video actions
          'video/setLocalStream', // Explicitly ignore MediaStream action
          'computer/', // All computer actions
          'computer/setMyStream', // Explicitly ignore MediaStream in setMyStream action
          'computer/addVideoStream', // Explicitly ignore MediaStream in addVideoStream action
          'virtualRoomAreas/', // All virtual room areas actions
          'virtualRoomFilter/', // All virtual room filter actions
        ],
        ignoredPaths: [
          'user.playerNameMap', // Map converted to object
          'room.availableRooms', // Room data with dates
          'webrtc', // All webrtc state that might contain media info
          'video.localStream', // Explicitly ignore MediaStream in video state
          'computer.myStream', // Explicitly ignore MediaStream in computer state
          'computer.peerStreams', // Explicitly ignore MediaStream in computer state
          'computer.shareScreenManager', // Ignore shareScreenManager instance
          'payload.stream', // Any action payload with a stream property
          'payload.call', // Explicitly ignore the MediaConnection object from PeerJS
          'payload.call._originalCall', // Explicitly ignore the original call reference
          'payload.*.stream', // Any nested action payload with a stream property
          'payload.id.stream', // Stream property in a nested id object
          'payload.id.call', // Call property in a nested id object
          'payload.*.createdAt', // Date objects in createdAt fields
          'payload.*.updatedAt', // Date objects in updatedAt fields
          'computer.peerStreams.*.stream', // More specific path for streams in peerStreams map
        ],
      },
    }),
})

// Expose store to window for debugging and for components to access
if (typeof window !== 'undefined') {
  ; (window as any).store = store
}

// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof store.getState>
// Inferred type: {posts: PostsState, comments: CommentsState, users: UsersState}
export type AppDispatch = typeof store.dispatch

export default store

export {
  openComputerDialog,
  closeComputerDialog,
  setMyStream,
  addVideoStream,
  removeVideoStream,
} from './ComputerStore'
